<template>
    <v-app>
        <!-- Application Bar -->
        <v-app-bar>
            <v-app-bar-nav-icon @click="toggleDrawer" class="toggle-button" />

            <v-img :src="isDarkTheme ? '/logo-horizontal-white.png' : '/logo-horizontal-black.png'"
                max-height="40"
                max-width="120"
                @click="$router.push('/')"
                style="cursor: pointer" />
            <span class="title-markazi ml-2">Intelligence Services - v{{ version }}</span>
            <v-spacer />

            <!-- Theme toggle -->
            <v-btn-toggle v-model="themeChoice" mandatory rounded="pill" density="comfortable" color="violean">
                <v-tooltip :text="$t('bar.lightTheme')">
                    <template v-slot:activator="{ props }">
                        <v-btn value="light" size="small" v-bind="props">
                            <v-icon>mdi-weather-sunny</v-icon>
                        </v-btn>
                    </template>
                </v-tooltip>

                <v-tooltip :text="$t('bar.autoTheme')">
                    <template v-slot:activator="{ props }">
                        <v-btn value="auto" size="small" v-bind="props">
                            <v-icon>mdi-theme-light-dark</v-icon>
                        </v-btn>
                    </template>
                </v-tooltip>

                <v-tooltip :text="$t('bar.darkTheme')">
                    <template v-slot:activator="{ props }">
                        <v-btn value="dark" size="small" v-bind="props">
                            <v-icon>mdi-weather-night</v-icon>
                        </v-btn>
                    </template>
                </v-tooltip>
            </v-btn-toggle>

            <!-- Locale toggle -->
            <v-btn-toggle v-model="localeChoice" mandatory rounded="pill" density="comfortable" color="violean"
                class="ml-2">
                <v-tooltip text="English">
                    <template v-slot:activator="{ props }">
                        <v-btn value="en" size="small" v-bind="props">
                            <span class="text-subtitle-2">🇺🇸</span>
                        </v-btn>
                    </template>
                </v-tooltip>

                <v-tooltip text="Français">
                    <template v-slot:activator="{ props }">
                        <v-btn value="fr" size="small" v-bind="props">
                            <span class="text-subtitle-2">🇫🇷</span>
                        </v-btn>
                    </template>
                </v-tooltip>
            </v-btn-toggle>
        </v-app-bar>

        <!-- Navigation Drawer -->
        <v-navigation-drawer v-model="drawer" :rail="rail" permanent>
            <v-list>
                <v-list-item :prepend-icon="rail ? 'mdi-chevron-right' : 'mdi-chevron-left'" title=""
                    @click.stop="rail = !rail" />
                <v-divider />
                <v-list-item to="/prompt" value="prompt" @click="handleItemClick">
                    <template v-slot:prepend>
                        <v-icon class="icon-class">mdi-message-text</v-icon>
                    </template>
                    <v-list-item-title class="menu-markazi">{{ $t('menu.prompt') }}</v-list-item-title>
                </v-list-item>
                <v-list-item to="/knowledge" value="knowledge" @click="handleItemClick">
                    <template v-slot:prepend>
                        <v-icon class="icon-class">mdi-brain</v-icon>
                    </template>
                    <v-list-item-title class="menu-markazi">{{ $t('menu.knowledge') }}</v-list-item-title>
                </v-list-item>
                <v-list-item to="/agent" value="agent" @click="handleItemClick">
                    <template v-slot:prepend>
                        <v-icon class="icon-class">mdi-robot</v-icon>
                    </template>
                    <v-list-item-title class="menu-markazi">{{ $t('menu.agent') }}</v-list-item-title>
                </v-list-item>
                <v-list-item to="/home" value="home" @click="handleItemClick">
                    <template v-slot:prepend>
                        <v-icon class="icon-class">mdi-home</v-icon>
                    </template>
                    <v-list-item-title class="menu-markazi">{{ $t('menu.dashboard') }}</v-list-item-title>
                </v-list-item>
            </v-list>

            <!-- Help menu at the bottom -->
            <template v-slot:append>
                <div class="pa-2">
                    <v-menu location="top" offset="5">
                        <template v-slot:activator="{ props }">
                            <v-list-item prepend-icon="mdi-help-circle" value="help" v-bind="props">
                                <template v-slot:prepend>
                                    <v-icon class="icon-class">mdi-help-circle</v-icon>
                                </template>
                                <v-list-item-title class="menu-markazi">Help</v-list-item-title>
                            </v-list-item>
                        </template>
                        <v-list density="compact" width="240">
                            <v-list-item @click="handleKeyboardShortcuts">
                                <template v-slot:prepend>
                                    <v-icon>mdi-keyboard</v-icon>
                                </template>
                                <v-list-item-title>Keyboard shortcuts</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="handleWhatsNew">
                                <template v-slot:prepend>
                                    <v-icon>mdi-new-box</v-icon>
                                </template>
                                <v-list-item-title>What's new</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="handleOpenAPI">
                                <template v-slot:prepend>
                                    <v-icon>mdi-api</v-icon>
                                </template>
                                <v-list-item-title>OpenAPI</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
            </template>
        </v-navigation-drawer>

        <!-- Main Content -->
        <v-main>
            <v-container fluid>
                <router-view />
            </v-container>
        </v-main>

        <!-- Global Alert -->
        <v-snackbar v-model="snackbar" :color="color" timeout="3000">
            {{ message }}
            <template v-slot:actions>
                <v-btn variant="text" @click="snackbar = false">Close</v-btn>
            </template>
        </v-snackbar>

        <!-- Changelog Dialog -->
        <v-dialog v-model="showChangelog" width="80%" class="changelog-dialog">
            <v-card class="changelog-card">
                <v-card-title class="text-h5 pa-4 d-flex align-center">
                    <span>What's New</span>
                    <v-spacer></v-spacer>
                    <v-btn icon="mdi-close" variant="text" @click="showChangelog = false" class="ms-auto"></v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-4 changelog-content">
                    <div v-html="markdownContent" class="markdown-body"></div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-app>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useTheme } from 'vuetify'
import { useI18n } from 'vue-i18n'
import { useSnackbarStore } from '@/stores/snackbarStore'
import { userStore } from '@/stores/userStore'
import { storeToRefs } from 'pinia'
import { version } from '../config/version'
import { marked } from 'marked'
import axios from 'axios'

// --- Drawer ---
const drawer = ref(true)
const rail = ref(false)

const toggleDrawer = () => {
    drawer.value = !drawer.value
}

// --- Theme ---
const theme = useTheme()
const themeChoice = ref(localStorage.getItem('themePreference') || 'auto')

// Watch for theme changes and save to localStorage
watch(themeChoice, (newValue) => {
    localStorage.setItem('themePreference', newValue)
    updateTheme(newValue)
})

// Function to update theme based on choice
const updateTheme = (choice: string) => {
    if (choice === 'auto') {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
        theme.global.name.value = prefersDark ? 'dark' : 'light'
    } else {
        theme.global.name.value = choice
    }
}

// Initialize theme on component mount
onMounted(() => {
    updateTheme(themeChoice.value)

    // Listen for system theme changes when in auto mode
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
        if (themeChoice.value === 'auto') {
            theme.global.name.value = e.matches ? 'dark' : 'light'
        }
    })
})

const isDarkTheme = computed(() => theme.global.current.value.dark)

const { locale } = useI18n()
const localeChoice = ref(localStorage.getItem('localePreference') || 'en')
const user = userStore()

watch(localeChoice, (newValue) => {
    localStorage.setItem('localePreference', newValue)
    locale.value = newValue
    // Update HTML lang attribute
    document.documentElement.lang = newValue
    // Store in user preferences
    user.setLang(newValue)
})

onMounted(() => {
    // Initialize locale from user store
    const storedLang = user.currentLang
    if (storedLang) {
        localeChoice.value = storedLang
        locale.value = storedLang
        document.documentElement.lang = storedLang
    }
})

// Get snackbar store
const snackbarStore = useSnackbarStore()
const { show: snackbar, message, color } = storeToRefs(snackbarStore)

const handleItemClick = (event: Event) => {
    // Prevent drawer from opening when in rail mode
    if (rail.value) {
        event.stopPropagation();
    }
};

const handleKeyboardShortcuts = () => {
    // Add keyboard shortcuts dialog or navigation
    console.log('Show keyboard shortcuts');
};

const showChangelog = ref(false)
const markdownContent = ref('')

const handleWhatsNew = async () => {
    try {
        const response = await axios.get('CHANGELOG.md')
        markdownContent.value = marked(response.data).toString()
        showChangelog.value = true
    } catch (error) {
        console.error('Error loading changelog:', error)
        message.value = 'Error loading changelog'
        color.value = 'error'
    }
}

const handleOpenAPI = () => {
    const url = new URL('/api/docs', window.location.origin);
    window.open(url.toString(), '_blank');
};
</script>

<style>
.changelog-dialog {
    height: 80vh;
    display: flex;
    align-items: center;
    justify-content: center;
}

.changelog-card {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.changelog-content {
    flex-grow: 1;
    overflow-y: auto;
}

.markdown-body {
    font-family: -apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji";
    font-size: 16px;
    line-height: 1.5;
    word-wrap: break-word;
}

.markdown-body h1,
.markdown-body h2,
.markdown-body h3,
.markdown-body h4,
.markdown-body h5,
.markdown-body h6 {
    margin-top: 24px;
    margin-bottom: 16px;
    font-weight: 600;
    line-height: 1.25;
}

.markdown-body h1 { font-size: 2em; }
.markdown-body h2 { font-size: 1.5em; }
.markdown-body h3 { font-size: 1.25em; }

.markdown-body p {
    margin-top: 0;
    margin-bottom: 16px;
}

.markdown-body ul,
.markdown-body ol {
    margin-top: 0;
    margin-bottom: 16px;
    padding-left: 2em;
}

.markdown-body code {
    padding: 0.2em 0.4em;
    margin: 0;
    font-size: 85%;
    background-color: rgba(175,184,193,0.2);
    border-radius: 6px;
}
</style>

<style scoped>
.toggle-button {
    position: relative;
    z-index: 1000;
}


</style>

<style>
.pt-sans-table {
    font-family: 'PT Sans', 'Lucida Sans Unicode' !important;
}

/* Target all cells including headers */
.pt-sans-table :deep(td),
.pt-sans-table :deep(th) {
    font-family: 'PT Sans', 'Lucida Sans Unicode' !important;
}

.title-markazi {
    font-family: 'Markazi Text', Georgia !important;
    align-items: center;
    display: flex;
    font-size: 30px;
    font-weight: semibold;
}

.sub-title-markazi {
    font-family: 'Markazi Text', Georgia !important;
    align-items: center;
    display: flex;
    font-size: 20px;
    font-weight: regular;
}

.menu-markazi {
    font-family: 'Markazi Text', Georgia !important;
    align-items: center;
    display: flex;
    font-size: 26px;
    font-weight: semibold;
}

.icon-class {
    font-weight: lighter;
}
</style>
